// Input capture: mouse, touch and keyboard folded into the agar-style "target point + buttons"
// model the contract exposes. Runtime-owned; the module only ever sees the coerced input object.
//
// Requirements this file exists to meet (GOTCHAS.md):
//   - clear held keys on blur and send a neutral input, or the stuck player walks forever;
//   - iOS replays a touch as a synthetic mouse event ~100 ms later: stamp touches in the capture
//     phase and read the stamp in every pointer handler;
//   - touch-first: the finger is the target, a second finger is button A.

export interface RawInput {
  tx: number;
  ty: number;
  a: boolean;
  b: boolean;
}

const TOUCH_SHADOW_MS = 700;
/** How far ahead the movement keys aim: far enough that every game reads it as "go that way". */
export const KEY_REACH = 400;
/**
 * How far ahead they aim while B is held: about one block. B is the "act on what is next to me"
 * button (dig, place, use), and a target 400 away is out of every game's reach; Burrow digs
 * within 150 and its blocks are 50, so S + B lands on the cell under your feet, and any
 * direction lands on the block you are standing against.
 */
export const KEY_AIM = 60;
/** Keys that hold B. The letter itself, because every label in the game calls the button B. */
export const B_KEYS = ["KeyB", "KeyE", "ShiftLeft", "ShiftRight"] as const;

/** Where the keys point from `self`, given a unit direction and whether B is held. */
export function keyTarget(self: { x: number; y: number }, dir: { x: number; y: number }, bHeld: boolean): { x: number; y: number } {
  const d = bHeld ? KEY_AIM : KEY_REACH;
  return { x: self.x + dir.x * d, y: self.y + dir.y * d };
}

export class InputController {
  private pointer: { x: number; y: number } | null = null;
  private pointerDown = false;
  private rightDown = false;
  private readonly keys = new Set<string>();
  private touches = 0;
  private lastTouchT = -Infinity;
  /** On-screen buttons held (touch devices). */
  private readonly held = new Set<string>();
  /** Set on blur until the next real input arrives. */
  private neutral = false;
  /** Something changed since the last time `current` was read; the caller may want to send at once. */
  urgent = false;

  /**
   * Every listener is tied to `signal`; abort it to detach the controller when the game unmounts.
   * `buttons` are optional on-screen A/B buttons (data-btn="a" | "b") for touch devices.
   */
  constructor(private readonly el: HTMLElement, signal: AbortSignal, buttons: HTMLElement[] = []) {
    const opts = { passive: false, signal } as AddEventListenerOptions;

    for (const b of buttons) {
      const name = b.dataset.btn ?? "";
      const down = (ev: Event) => {
        ev.preventDefault();
        this.neutral = false;
        this.held.add(name);
      };
      const up = () => this.held.delete(name);
      b.addEventListener("pointerdown", down, opts);
      b.addEventListener("pointerup", up, { signal });
      b.addEventListener("pointercancel", up, { signal });
      b.addEventListener("pointerleave", up, { signal });
      b.addEventListener("contextmenu", (ev) => ev.preventDefault(), { signal });
    }

    // Capture-phase stamps so the shadow is set before any pointer handler runs.
    for (const type of ["touchstart", "touchmove", "touchend", "touchcancel"]) {
      el.addEventListener(
        type,
        (ev) => {
          this.lastTouchT = performance.now();
          this.touches = (ev as TouchEvent).touches.length;
          if (type === "touchstart" || type === "touchmove") ev.preventDefault();
        },
        { capture: true, passive: false, signal },
      );
    }

    const onPointer = (ev: PointerEvent) => {
      if (ev.pointerType === "mouse" && performance.now() - this.lastTouchT < TOUCH_SHADOW_MS) return;
      this.neutral = false;
      const right = ev.button === 2 || (ev.buttons & 2) !== 0;
      if (ev.type === "pointerdown") {
        if (ev.button === 2) this.rightDown = true;
        else this.pointerDown = true;
      } else if (ev.type === "pointerup" || ev.type === "pointercancel") {
        if (ev.button === 2) this.rightDown = false;
        else this.pointerDown = false;
        if (ev.type === "pointercancel") this.rightDown = false;
      } else if (ev.type === "pointermove" && ev.pointerType === "mouse") {
        // Buttons released outside the element never send pointerup; trust the live button state.
        this.pointerDown = (ev.buttons & 1) !== 0;
        this.rightDown = right;
      }
      if (ev.type === "pointercancel") this.pointer = null;
      else this.pointer = { x: ev.clientX, y: ev.clientY };
    };
    for (const type of ["pointerdown", "pointermove", "pointerup", "pointercancel"]) {
      el.addEventListener(type, onPointer as EventListener, opts);
    }
    el.addEventListener("pointerleave", () => {
      this.pointerDown = false;
    }, { signal });
    el.addEventListener("contextmenu", (ev) => ev.preventDefault(), { signal });

    window.addEventListener("keydown", (ev) => {
      if (ev.repeat) return;
      if (ev.target instanceof HTMLInputElement || ev.target instanceof HTMLTextAreaElement) return;
      if (ev.code === "Space") ev.preventDefault();
      this.neutral = false;
      this.keys.add(ev.code);
    }, { signal });
    window.addEventListener("keyup", (ev) => this.keys.delete(ev.code), { signal });

    const clear = () => {
      this.keys.clear();
      this.held.clear();
      this.pointerDown = false;
      this.rightDown = false;
      this.touches = 0;
      this.neutral = true;
      this.urgent = true;
    };
    window.addEventListener("blur", clear, { signal });
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) clear();
    }, { signal });
  }

  /** The input to send now. `self` is your entity's rendered position; `toWorld` maps screen to world. */
  current(self: { x: number; y: number } | undefined, toWorld: (sx: number, sy: number) => { x: number; y: number }): RawInput {
    this.urgent = false;
    if (!self) return { tx: 0, ty: 0, a: false, b: false };
    if (this.neutral) return { tx: self.x, ty: self.y, a: false, b: false };

    // A: space, left button, a second finger, or the on-screen A. B: the B key, E, shift, right button, or the on-screen B.
    const a = this.keys.has("Space") || this.pointerDown || this.touches >= 2 || this.held.has("a");
    const b = B_KEYS.some((k) => this.keys.has(k)) || this.rightDown || this.held.has("b");

    let tx = self.x;
    let ty = self.y;
    const dir = this.keyDirection();
    if (dir) {
      const t = keyTarget(self, dir, b);
      tx = t.x;
      ty = t.y;
    } else if (this.pointer) {
      const p = toWorld(this.pointer.x, this.pointer.y);
      tx = p.x;
      ty = p.y;
    }
    return { tx: Math.round(tx * 10) / 10, ty: Math.round(ty * 10) / 10, a, b };
  }

  private keyDirection(): { x: number; y: number } | null {
    const k = this.keys;
    let x = 0;
    let y = 0;
    if (k.has("KeyW") || k.has("ArrowUp")) y -= 1;
    if (k.has("KeyS") || k.has("ArrowDown")) y += 1;
    if (k.has("KeyA") || k.has("ArrowLeft")) x -= 1;
    if (k.has("KeyD") || k.has("ArrowRight")) x += 1;
    if (!x && !y) return null;
    const len = Math.hypot(x, y);
    return { x: x / len, y: y / len };
  }
}
